
import { Vue, Options, prop } from "vue-class-component";
import ModalSuccessReceipt from "./modal-success-receipt.vue";
import formatInputMoney from "@/app/infrastructures/misc/common-library/FormatInputMoney";
import formatDate from "@/app/infrastructures/misc/common-library/FormatDate";
import {
  formatInputMoneyMYR,
  parseInputMoneyMYR,
  parsingErrorResponse
} from "@/app/infrastructures/misc/Utils";
import { PrintReceiptController } from "@/app/ui/controllers/PrintReceiptController";
import { SettlementInvoiceRequest, ActivityRequest } from "@/data/payload/api/PrintReceiptApiRequest";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { ErrorMessageEntities } from "@/domain/entities/MainApp";
import { Invoice, InvoiceData, PaymentHistory, SuccessInvoiceData } from "@/domain/entities/PrintReceipt";
import InvoicePdfA4 from "./invoice/InvoicePdfA4.vue";
import InvoicePdfStruk from "./invoice/InvoicePdfStruk.vue";
import formatPrice from '@/app/infrastructures/misc/common-library/v2/FormatPrice';
import { configPrice } from '@/app/infrastructures/misc/common-library/modules/v2/Price';
import { AccountController } from '@/app/ui/controllers/AccountController';

class Props {
  invoiceData = prop<InvoiceData>({
    default: () => ({}),
    type: Object
  });
}

@Options({
  name: "ModalUpdateInvoice",
  components: {
    ModalSuccessReceipt,
    InvoicePdfA4,
    InvoicePdfStruk
  },
  emits: ["close", "success"]
})
export default class ModalUpdateInvoice extends Vue.with(Props) {
  paymentAmount = "";

  isLoading = false;
  showSuccessModal = false;
  successData: SuccessInvoiceData = new SuccessInvoiceData();
  paymentDetailData = null;

  mounted() {
    this.fetchPaymentDetail()
  }

  get accountCurrency() {
    return (
      (AccountController.accountData?.accountCurrency as "IDR" | "MYR") ?? "IDR"
    );
  }

  get totalPaidAmount() {
    if (
      !this.invoiceData.paymentHistory ||
      this.invoiceData.paymentHistory.length === 0
    ) {
      return 0;
    }
    return this.invoiceData.paymentHistory.reduce(
      (acc: number, curr: any) => acc + (curr.amount || 0),
      0
    );
  }

  get remainingAmount() {
    const total = this.invoiceData.totalAmount || 0;
    const paid = this.totalPaidAmount;
    return total - paid;
  }

  get paymentAmountValue() {
    if (this.accountCurrency === "MYR") {
      return parseInputMoneyMYR(this.paymentAmount);
    }
    return Number(String(this.paymentAmount ?? "").replace(/\./g, "").replace(/[^0-9]/g, ""));
  }

  get isPaymentExceedsRemaining() {
    return this.paymentAmountValue > this.remainingAmount;
  }

  get isDisabled() {
    const amount = this.paymentAmountValue;
    const remaining = this.remainingAmount;

    return amount <= 0 || amount > remaining;
  }

  get currencyPrefix() {
    return configPrice[this.accountCurrency]?.prefix ?? "Rp";
  }

  formatCurrency(value: number) {
    return formatPrice(value, "", this.accountCurrency as "IDR" | "MYR");
  }

  formatMoney(value: any) {
    if (this.accountCurrency === "MYR") {
      return formatInputMoneyMYR(value, 2);
    }
    const val = String(value).replace(/[\D]+/g, "");
    return formatInputMoney(val);
  }

  formatDateHistory(date: string) {
    return formatDate(date, "DD MMMM YYYY, HH:mm");
  }

  get maskingPhoneNumber() {
    const phone = String(this.invoiceData?.customerPhone ?? "");
    if (!phone) return "";
    return phone.replace(
      /^(\d{2})(\d+)(\d{3})$/,
      (_, start, middle, end) => start + "*".repeat(middle.length) + end
    );
  }

  onClose() {
    this.$emit("close");
  }

  get invoiceDetail() {
    return PrintReceiptController.invoiceDetailData;
  }

  async handleDownload(option: { key: string }) {
    // Options: 'pdf_a4', 'png_a4', 'struk'
    try {
      MainAppController.showLoading();
      const detail = await PrintReceiptController.getDetailInvoice(this.invoiceData.id);
      PrintReceiptController.setInvoiceDetailData(
        new Invoice({
          ...detail,
          header: {
            ...detail.header,
            invoiceId: this.invoiceData.id
          }
        })
      );
      if (option.key === 'pdf_a4') {
        await (this.$refs as any).invoice.downloadPdfA4();
      } else if (option.key === 'png_a4') {
        await (this.$refs as any).invoice.downloadPngA4();
      } else if (option.key === 'struk') {
        await (this.$refs as any).struk.downloadPdfStruk();
      }
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          this.$t("tagihan.errorGetDetail") as string,
          () => {
            MainAppController.closeErrorMessage();
            this.handleDownload(option);
          }
        )
      )
    } finally {
      MainAppController.closeLoading();
    }
    PrintReceiptController.activity({
      id: this.invoiceData.id,
      payload: new ActivityRequest(true)
    });
  }

  async fetchPaymentDetail() {
    try {
      const response = await PrintReceiptController.getPaymentDetailInvoice(this.invoiceData.id)
      const { data } = response || {};
      
      if(data.code === 200) {
        if(data?.data?.payment_history?.length) {
          this.invoiceData.paymentHistory = data?.data?.payment_history.map((item: any) => {
            return new PaymentHistory({
              date: item.paid_at,
              amount: item.amount,
              user: item.created_by_name
            })
          })
        } else {
          this.invoiceData.paymentHistory = []
        }

        this.invoiceData.invoiceNumber = data?.data?.header?.invoice_no;
        this.invoiceData.customerName = data?.data?.header?.billed_to_name
        this.invoiceData.customerPhone = data?.data?.header?.billed_to_phone
        this.invoiceData.totalAmount = data?.data?.header?.total_amount
        this.invoiceData.totalPaid = data?.data?.header?.total_paid
        this.invoiceData.balanceDue = data?.data?.header?.balance_due
        this.invoiceData.totalStt = data?.data?.header?.total_stt
        this.invoiceData.status = data?.data?.header?.status === 'partially_paid' ? 'PARTIAL' : data?.data?.header?.status === 'unpaid' ? 'UNPAID' : 'PAID'
      }
    } catch (error) {
      MainAppController.showErrorMessage(
        new ErrorMessageEntities({
          title: "Update Gagal",
          message: (error as any)?.message || "Gagal mengambil data detail.",
          onClose: () => MainAppController.closeErrorMessage()
        })
      );
    }
  }

  async onSubmit() {
    const amount = this.paymentAmountValue;
    this.isLoading = true;
    try {
      const payload = new SettlementInvoiceRequest(amount);
      const response = await PrintReceiptController.settlementInvoice({
        id: this.invoiceData.id,
        payload: payload
      });

      let currentHistory: any[] = [];
      if (response.data && response.data.payment_history) {
        currentHistory = response.data.payment_history;
      } else {
         currentHistory = this.invoiceData.paymentHistory || [];
      }

      const totalPaid = currentHistory.reduce((sum: number, item: any) => sum + item.amount, 0);
      
      this.successData = new SuccessInvoiceData({
        invoiceNo: this.invoiceData.invoiceNumber || "",
        bookedBy: this.invoiceData.customerName || "",
        totalStt: this.invoiceData.totalStt || 0,
        bookedByPhone: this.invoiceData.customerPhone || "",
        totalAmount: this.invoiceData.totalAmount || 0,
        balanceDue: (this.invoiceData.totalAmount || 0) - totalPaid
      });

      this.$emit("success", response);
      this.showSuccessModal = true;
    } catch (error) {
      MainAppController.showErrorMessage(
        new ErrorMessageEntities({
          title: "Update Gagal",
          message: (error as any)?.message || "Gagal update status lunas",
          onClose: () => MainAppController.closeErrorMessage()
        })
      );
    } finally {
      this.isLoading = false;
    }
  }
}
